import React from "react";
import { PinnedIcon } from "../../Core/svgV2/Pinned";

const URL_REGEX =
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)/i;

const PhotoIcon = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <rect x="1.5" y="2.5" width="13" height="11" rx="1.5" stroke="#165ABF" strokeWidth="1.2" />
        <circle cx="5.2" cy="6" r="1.2" stroke="#165ABF" strokeWidth="1.1" />
        <path d="M2 11.5L5.5 8.5L8 10.5L11 7L14 10" stroke="#165ABF" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const VideoIcon = () => (
    <svg width="14" height="13" viewBox="0 0 16 14" fill="none">
        <rect x="1" y="2" width="9.5" height="10" rx="1.5" stroke="#165ABF" strokeWidth="1.2" />
        <path d="M10.5 6L14.5 3.5V10.5L10.5 8" stroke="#165ABF" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
);

const DocIcon = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <path d="M3.5 1.5h6l3 3v9a1 1 0 01-1 1h-8a1 1 0 01-1-1v-11a1 1 0 011-1z" stroke="#165ABF" strokeWidth="1.1" />
        <path d="M5.5 8h5M5.5 10.5h5M5.5 5.5h2.5" stroke="#165ABF" strokeWidth="1" strokeLinecap="round" />
    </svg>
);

const LinkIcon = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <path
            d="M6.5 9.5l3-3M6 5.2L7 4a2.3 2.3 0 013.3 3.2L9 8.5M10 10.8L9 12a2.3 2.3 0 01-3.3-3.2l1-1"
            stroke="#165ABF"
            strokeWidth="1.2"
            strokeLinecap="round"
        />
    </svg>
);

function resolvePinnedContent(pinnedMessage: any): { type: string; label: string; thumbnail?: string } {
    const attachments = pinnedMessage?.attachments ?? [];

    const photos = attachments.filter(
        (a: any) =>
            a.type?.startsWith("image") ||
            (!a.type?.startsWith("video") && a.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i))
    );
    const videos = attachments.filter(
        (a: any) => a.type?.startsWith("video") || a.url?.match(/\.(mp4|mov|avi|mkv)$/i)
    );
    const docs = attachments.filter(
        (a: any) =>
            a.type?.startsWith("application") ||
            a.type?.startsWith("text") ||
            a.url?.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|csv)$/i)
    );

    const photoCount = photos.length;
    const videoCount = videos.length;
    const docCount = docs.length;
    const totalMedia = photoCount + videoCount;

    if (docCount > 0 && totalMedia === 0) {
        const name = docCount === 1 ? (docs[0]?.name ?? docs[0]?.fileName ?? "Document") : `${docCount} Documents`;
        return { type: "doc", label: name, thumbnail: docCount === 1 ? docs[0]?.thumbnail : undefined };
    }

    if (videoCount > 0 && photoCount === 0 && docCount === 0) {
        return {
            type: "video",
            label: videoCount === 1 ? "Video" : `${videoCount} Videos`,
            thumbnail: videos[0]?.thumbnail || videos[0]?.url,
        };
    }

    if (photoCount > 0 && videoCount === 0 && docCount === 0) {
        return {
            type: "photo",
            label: photoCount === 1 ? "Photo" : `${photoCount} Photos`,
            thumbnail: photos[0]?.thumbnail || photos[0]?.url,
        };
    }

    if (attachments.length > 0) {
        const parts: string[] = [];
        if (photoCount > 0) parts.push(photoCount === 1 ? "1 Photo" : `${photoCount} Photos`);
        if (videoCount > 0) parts.push(videoCount === 1 ? "1 Video" : `${videoCount} Videos`);
        if (docCount > 0) parts.push(docCount === 1 ? "1 Document" : `${docCount} Documents`);
        return { type: "photo", label: parts.join(", ") || "Attachment", thumbnail: attachments[0]?.thumbnail || attachments[0]?.url };
    }

    const text = pinnedMessage?.content || pinnedMessage?.message || "";
    if (!text) return { type: "text", label: "Pinned message" };
    if (URL_REGEX.test(text)) return { type: "link", label: text };
    return { type: "text", label: text };
}

function ContentIcon({ type }: { type: string }) {
    switch (type) {
        case "photo":
            return <PhotoIcon />;
        case "video":
            return <VideoIcon />;
        case "doc":
            return <DocIcon />;
        case "link":
            return <LinkIcon />;
        default:
            return null;
    }
}

const PinnedMessageBanner = ({ pinnedMessage, onClick }: { pinnedMessage: any; onClick: () => void }) => {
    if (!pinnedMessage) return null;

    const { type, label, thumbnail } = resolvePinnedContent(pinnedMessage);

    return (
        <div
            className="flex items-center gap-2 bg-white border-b border-gray-200 px-4 py-2 cursor-pointer hover:bg-gray-50 transition-colors flex-shrink-0"
            onClick={onClick}
        >
            <div className="w-8 h-8 rounded-[8px] bg-primary-50 flex items-center justify-center flex-shrink-0">
                <PinnedIcon className="text-primary-600" />
            </div>

            <div className="flex-1 min-w-0 flex items-center gap-1.5">
                <ContentIcon type={type} />
                <span className="out-500-14 text-gray-700 truncate">{label}</span>
            </div>

            {thumbnail && (
                <div
                    className="w-10 h-10 rounded-[4px] bg-no-repeat bg-center bg-cover flex-shrink-0"
                    style={{ backgroundImage: `url(${thumbnail})` }}
                />
            )}
        </div>
    );
};

export default React.memo(PinnedMessageBanner);